import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SearchableTemplateList, TemplateList, TemplateListItem } from './template-list';
import { WEXLER_PROMPT_TEMPLATES } from './wexler-prompt-templates';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeletePromptTemplate } from '@/api/mutations/useDeletePromptTemplate';
import { useGetPromptTemplates } from '@/api/queries/useGetPromptTemplates';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';

interface TemplatesPopoverProps {
  onSelect: (body: string) => void;
  onOpenTemplateBuilder: () => void;
  onEditTemplate: (template: { id: string; matterId?: string; title: string; body: string }) => void;
}

export function TemplatesPopover({ onSelect, onOpenTemplateBuilder, onEditTemplate }: TemplatesPopoverProps) {
  const { matterId } = useParams();
  const { data: userTemplates = [] } = useGetPromptTemplates({
    matterId,
    enabled: true,
  });
  const { mutate: deleteTemplate, isLoading: isDeleting } = useDeletePromptTemplate(matterId);
  const [deletingTemplate, setDeletingTemplate] = useState<TemplateListItem | null>(null);
  const [showOtherMatterTemplates, setShowOtherMatterTemplates] = useState(false);

  const wexlerTemplateItems = useMemo<TemplateListItem[]>(
    () =>
      WEXLER_PROMPT_TEMPLATES.map((t) => ({
        id: t.id,
        label: t.label,
        icon: t.icon,
        body: t.template,
        source: 'wexler',
      })),
    [],
  );

  const personalTemplateItems = useMemo<TemplateListItem[]>(
    () =>
      userTemplates
        .filter((t) => showOtherMatterTemplates || t.matterId === matterId)
        .map((t) => ({
          id: t.id,
          matterId: t.matterId,
          label: t.title,
          body: t.body,
          source: 'personal',
          createdDate: t.createdDate,
        })),
    [matterId, showOtherMatterTemplates, userTemplates],
  );

  const handleSelect = (template: TemplateListItem) => {
    trackCustomEvent(TrackingEventsV2.KimUseTemplate, {
      matterId,
      templateSource: template.source ?? 'personal',
    });
    onSelect(template.body);
  };

  const handleConfirmDelete = () => {
    if (!deletingTemplate) return;
    deleteTemplate(
      { id: deletingTemplate.id, templateMatterId: deletingTemplate.matterId },
      {
        onSuccess: () => setDeletingTemplate(null),
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Error deleting template. Please try again.');
        },
      },
    );
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant={'ghost'} size={'sm'}>
            <NewChip className="relative top-auto right-auto" />
            Templates
            <Icon className="lucide--chevron-down" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="-mt-1 -mr-2 w-[26rem] max-w-none p-0"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <Tabs defaultValue="wexler">
            <TabsList className="h-8 w-full justify-start gap-1 p-5 px-3">
              <TabsTrigger value="wexler" className="px-2 py-3 pb-2 text-[13px]">
                Wexler
              </TabsTrigger>
              <TabsTrigger value="personal" className="px-2 py-3 pb-2 text-[13px]">
                Personal
              </TabsTrigger>
            </TabsList>

            <TabsContent value="wexler" className="m-0 max-h-80 overflow-y-auto">
              <TemplateList
                templates={wexlerTemplateItems}
                emptyMessage="No Wexler templates"
                onSelect={handleSelect}
              />
            </TabsContent>

            <TabsContent value="personal" className="m-0 max-h-80 overflow-y-auto">
              <SearchableTemplateList
                templates={personalTemplateItems}
                emptyMessage={
                  showOtherMatterTemplates ? 'No personal templates' : 'No personal templates in this Matter'
                }
                onSelect={handleSelect}
                onEdit={(t) => onEditTemplate({ id: t.id, matterId: t.matterId, title: t.label, body: t.body })}
                onDelete={(t) => setDeletingTemplate(t)}
              />

              <div className="m-1.75 mt-0 flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.75">
                <Checkbox
                  id="show-other-matter-templates"
                  checked={showOtherMatterTemplates}
                  onCheckedChange={(checked) => setShowOtherMatterTemplates(checked === true)}
                  className="scale-80 bg-white"
                />
                <label
                  htmlFor="show-other-matter-templates"
                  className="mt-px text-xs leading-none text-gray-400 select-none"
                >
                  Include templates from other Matters
                </label>
              </div>
            </TabsContent>
          </Tabs>

          <div>
            <hr />
            <button
              onClick={onOpenTemplateBuilder}
              className="m-1.5 mt-1.25 flex w-full items-center gap-2 rounded-sm px-2 py-1.25 text-left text-[13px] text-gray-800 duration-150 hover:bg-gray-50"
            >
              <Icon className="lucide--plus" /> Build a new template
            </button>
            <div className="flex gap-2 px-4 pb-3 text-xs text-gray-400">
              <Icon className="lucide--sparkle text-[11px]" />
              <p className="-mt-0.5">
                You can also generate templates from existing answers using the{' '}
                <Icon className="lucide--layout-template mx-0.25 -mb-0.5 inline-flex text-teal-500" /> button
              </p>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <ConfirmationModal
        isOpen={deletingTemplate !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingTemplate(null);
        }}
        title="Delete template"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      >
        Are you sure you want to delete <strong>{deletingTemplate?.label}</strong>? This cannot be undone.
      </ConfirmationModal>
    </>
  );
}
